/**
 * ShareDraftModal — share a saved draft with a professional (Sharing Phase A).
 *
 * One modal for the whole share lifecycle: pick a professional + access level,
 * see who already has access, change nothing silently — the backend notifies
 * NEW recipients (in-app + email). Corporate owner only; personal drafts are
 * unshareable (the backend enforces it; callers should disable the affordance).
 *
 * Mounted from MyDocumentsPage rows and the draft modals.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { FiX, FiUserPlus, FiTrash2 } from 'react-icons/fi';
import Button from '../common/Button';
import { Select as FormSelect } from '../common/FormControls';
import HoverPopover from '../common/HoverPopover';
import { draftsAPI, draftUpsiAPI, professionalInvitationAPI, professionalAssignmentAPI } from '../../services/api';
import { useUI } from '../../context/UIContext';

const LEVEL_OPTIONS = [
  { value: 'read', label: 'Read Only' },
  { value: 'read_write', label: 'Read & Write — can edit' },
];

/**
 * Asked at the point of sharing, because that is when the person knows the
 * answer and when it has consequences.
 *
 * Only a person answers this — nothing guesses. A wrong "contains UPSI" puts
 * routine paperwork into a SEBI register that cannot be edited afterwards; a
 * wrong "no" leaves a real sharing unrecorded. "Not sure" is a real answer
 * rather than a cop-out: it routes the decision to the compliance officer,
 * which is whose job it is.
 */
const UPSI_OPTIONS = [
  {
    value: 'not_upsi',
    label: 'No — ordinary company document',
    hint: 'Resolutions, filings, address changes, anything built from public records.',
  },
  {
    value: 'upsi',
    label: 'Yes — it contains unpublished price sensitive information',
    hint: 'Unannounced results, a dividend proposal, an acquisition, an undisclosed KMP change.',
  },
  {
    value: 'unsure',
    label: 'Not sure — ask the compliance officer',
    hint: 'A suggested register entry is raised for them to decide.',
  },
];

const levelLabel = (level) => (level === 'read_write' ? 'Can edit' : 'Read-only');

/**
 * Document types are stored as slugs. Showing the slug raw ("board_meeting")
 * reads as a database field leaking into the page — and this modal is where
 * someone decides whether a document carries UPSI, so what it is should be
 * legible at a glance.
 */
const docTypeLabel = (type) => String(type || '')
  .replace(/[_-]+/g, ' ')
  .replace(/^\w/, (c) => c.toUpperCase());

/** The day access was granted — the date that lands in the register. */
const sharedOn = (value) => (value
  ? new Date(value).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
  : '');

export default function ShareDraftModal({ open, draft, onClose, onChanged }) {
  const { showToast } = useUI();
  // Two-tier picker: default to the professionals this corporate already works
  // with (active, accepted assignments); the full platform directory sits
  // behind an explicit toggle — sharing statutory PII with a stranger should
  // be a deliberate act, not the default list.
  const [myPros, setMyPros] = useState([]);
  const [allPros, setAllPros] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const [loadingPros, setLoadingPros] = useState(false);
  // Directory search (all-professionals tier): hits the server so name, email
  // and membership number all match — while the RESPONSE stays name+profession
  // only. You can find someone by their email; emails are never listed out.
  const [proQuery, setProQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const searchTimerRef = useRef(null);
  const [selected, setSelected] = useState(null);
  const [level, setLevel] = useState('read');
  const [shares, setShares] = useState([]);
  const [loadingShares, setLoadingShares] = useState(false);
  const [busy, setBusy] = useState(false); // share / revoke in flight
  // Reg 3(5) classification. Always shown, with any previous answer selected —
  // a classification is a judgment someone may need to correct, and correcting
  // it sweeps the people who already hold the draft.
  const [upsiStatus, setUpsiStatus] = useState('');

  const professionals = showAll ? allPros : myPros;
  const myProIds = new Set(myPros.map((p) => p.value));
  const selectedIsStranger = Boolean(selected) && showAll && !myProIds.has(selected.value);

  const loadShares = useCallback(async () => {
    if (!draft?._id) return;
    setLoadingShares(true);
    try {
      const res = await draftsAPI.getDraftShares(draft._id);
      if (res?.success) setShares(res.data?.sharedWith || []);
    } catch (err) {
      // Non-fatal: the share form still works without the list.
      console.error('Failed to load shares:', err);
    } finally {
      setLoadingShares(false);
    }
  }, [draft?._id]);

  const loadProfessionals = useCallback(async () => {
    setLoadingPros(true);
    try {
      const [mineRes, allRes] = await Promise.allSettled([
        professionalAssignmentAPI.getMyAssignments({ status: 'active', professionalStatus: 'accepted' }),
        professionalInvitationAPI.searchProfessionals({ q: '' }),
      ]);

      let mine = [];
      if (mineRes.status === 'fulfilled' && mineRes.value?.success) {
        const seen = new Set();
        mine = (mineRes.value.data?.assignments || [])
          .map((a) => a.professional)
          .filter((p) => p && p._id && !seen.has(String(p._id)) && seen.add(String(p._id)))
          .map((p) => ({
            value: p._id,
            label: `${p.fullName || 'Unknown'} - ${p.profession || 'Not specified'}`,
          }));
      }
      setMyPros(mine);

      if (allRes.status === 'fulfilled' && allRes.value?.success) {
        setAllPros(
          (allRes.value.data?.professionals || [])
            .filter((p) => p && p._id)
            .map((p) => ({
              value: p._id,
              label: `${p.fullName || 'Unknown'} - ${p.profession || 'Not specified'}`,
            })),
        );
      }
      // Nothing connected yet → the directory is the only useful list.
      if (!mine.length) setShowAll(true);
    } catch (err) {
      console.error('Failed to load professionals:', err);
    } finally {
      setLoadingPros(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setSelected(null);
    setLevel('read');
    setShares([]);
    setShowAll(false);
    setProQuery('');
    // Blank when nobody has answered yet, so the question is put rather than
    // pre-answered — a default of "no" would silently opt every draft out.
    setUpsiStatus(
      draft?.upsi?.status && draft.upsi.status !== 'unclassified' ? draft.upsi.status : '',
    );
    loadShares();
    loadProfessionals();
  }, [open, draft, loadShares, loadProfessionals]);

  // Debounced directory search while the all-professionals tier is open.
  // Empty query falls back to the default first-20 list.
  useEffect(() => {
    if (!open || !showAll) return undefined;
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await professionalInvitationAPI.searchProfessionals({ q: proQuery.trim() });
        if (res?.success) {
          setAllPros(
            (res.data?.professionals || [])
              .filter((p) => p && p._id)
              .map((p) => ({
                value: p._id,
                label: `${p.fullName || 'Unknown'} - ${p.profession || 'Not specified'}`,
              })),
          );
        }
      } catch { /* keep the previous list */ } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(searchTimerRef.current);
  }, [open, showAll, proQuery]);

  // Escape closes (unless a call is in flight).
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, busy, onClose]);

  const handleShare = async () => {
    if (!selected) { showToast('Please select a professional', 'error'); return; }
    if (!upsiStatus) {
      showToast('Say whether this document contains UPSI before sharing it', 'error');
      return;
    }
    setBusy(true);
    try {
      // Classify BEFORE sharing. The backend reads the classification at the
      // moment of the share to decide whether to suggest a register entry, so
      // doing it after would miss this share and only catch the next one.
      await draftUpsiAPI.classify(draft._id, { status: upsiStatus });

      const res = await draftsAPI.shareDraft(draft._id, { userId: selected.value, accessLevel: level });
      if (res?.success) {
        showToast(
          upsiStatus === 'not_upsi'
            ? 'Shared — they’ll get a notification and an email'
            : 'Shared. A suggested entry is waiting on the UPSI Register → Pending tab.',
          'success',
        );
        setSelected(null);
        setLevel('read');
        setShares(res.data?.sharedWith || []);
        onChanged?.(draft._id, { visibility: res.data?.visibility || 'restricted' });
        loadShares(); // repopulate names (share response carries ids only)
      } else {
        showToast(res?.message || 'Failed to share', 'error');
      }
    } catch (err) {
      showToast(err.message || 'Failed to share', 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleRevoke = async (userId) => {
    setBusy(true);
    try {
      const res = await draftsAPI.unshareDraft(draft._id, userId);
      if (res?.success) {
        showToast('Access removed', 'success');
        setShares(res.data?.sharedWith || []);
        onChanged?.(draft._id, { visibility: res.data?.visibility });
        loadShares();
      } else {
        showToast(res?.message || 'Failed to remove access', 'error');
      }
    } catch (err) {
      showToast(err.message || 'Failed to remove access', 'error');
    } finally {
      setBusy(false);
    }
  };

  if (!open || !draft) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-in fade-in duration-150"
      style={{ background: 'rgba(8,17,31,0.55)' }}
    >
      <div className="bg-white rounded-[12px] w-full max-w-lg max-h-[90vh] overflow-y-auto animate-in fade-in zoom-in-95 duration-150" style={{ border: '1px solid #ddd6c6' }}>
        <div className="p-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-[18px] font-bold text-[#08111f]">Share Document</h2>
            <HoverPopover variant="tooltip" content="Close (Esc)">
              <button
                aria-label="Close"
                className="group w-8 h-8 rounded-[10px] flex items-center justify-center text-[#7a7060] transition-all duration-200 hover:bg-[#9a564a]/10 hover:text-[#9a564a] active:scale-90"
                onClick={onClose}
                disabled={busy}
              >
                <FiX size={17} className="transition-transform duration-300 group-hover:rotate-90" />
              </button>
            </HoverPopover>
          </div>

          {/* Document info.
              Titles repeat — a company raises "Company Secretary Appointment"
              many times — so the reference is what actually tells someone which
              document they are about to hand over. It is also what appears on
              the register entry, so the two can be matched up afterwards. */}
          <div className="mb-4">
            <div className="px-3 py-[8px] rounded-[8px] bg-[#f8f5ef] text-[12px]">
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className="font-semibold">{draft.documentTitle}</span>
                {draft.refNumber && (
                  <span className="text-[10.5px] font-mono" style={{ color: '#a09684' }}>
                    {draft.refNumber}
                  </span>
                )}
              </div>
              <div className="text-[11px]" style={{ color: '#7a7060' }}>
                {draft.companyName || 'No company'}
                {draft.documentType ? ` • ${docTypeLabel(draft.documentType)}` : ''}
              </div>
            </div>
          </div>

          {/* Share with someone new — two-tier: your professionals first, the
              platform directory behind an explicit toggle. */}
          <div className="mb-4">
            <label className="block text-[12px] font-semibold text-[#08111f] mb-2">
              {showAll ? 'Share with any professional on the platform' : 'Share with your professionals'}
            </label>
            {showAll && (
              <input
                className="w-full mb-2 px-2.5 py-[7px] rounded-[8px] border text-[12px] bg-white focus:outline-none focus:border-[#145f5f]"
                style={{ borderColor: '#ddd6c6' }}
                value={proQuery}
                onChange={(e) => setProQuery(e.target.value)}
                placeholder="Search by name, email or membership number…"
              />
            )}
            <FormSelect
              value={selected?.value || ''}
              onChange={(val) => setSelected(professionals.find((p) => p.value === val) || null)}
              options={professionals}
              placeholder={loadingPros || searching
                ? 'Loading professionals…'
                : professionals.length
                  ? 'Select a professional'
                  : showAll ? 'No professionals match' : 'No connected professionals yet'}
            />
            {myPros.length > 0 && (
              <button
                type="button"
                className="mt-1.5 text-[11px] font-semibold text-[#145f5f] hover:underline"
                onClick={() => {
                  const next = !showAll;
                  setShowAll(next);
                  // Keep the selection only if it exists in the list we're switching to.
                  const nextList = next ? allPros : myPros;
                  if (selected && !nextList.some((p) => p.value === selected.value)) setSelected(null);
                }}
              >
                {showAll ? '← Back to your professionals' : 'Can’t find them? Search all professionals on ComplyV'}
              </button>
            )}
            {selectedIsStranger && (
              <div className="mt-2 px-3 py-2 rounded-[8px] text-[11px] leading-relaxed" style={{ background: '#fff7e6', color: '#8a6a1f', border: '1px solid #eadfc0' }}>
                You haven’t worked with this professional before. Sharing gives them access to this
                document (company letterhead details included) as soon as you confirm.
              </div>
            )}
          </div>
          <div className="mb-5">
            <label className="block text-[12px] font-semibold text-[#08111f] mb-2">Access level</label>
            <FormSelect value={level} onChange={setLevel} options={LEVEL_OPTIONS} />
          </div>

          {/* SEBI (PIT) Reg 3(5). Asked at the point of sharing because that is
              when the person knows, and required because a silent default would
              either flood the register with routine paperwork or leave real
              sharings unrecorded. */}
          <div className="mb-5 pt-4 border-t" style={{ borderColor: '#eee8db' }}>
            <label className="block text-[12px] font-semibold text-[#08111f] mb-1">
              Does this document contain unpublished price sensitive information?
            </label>
            <p className="text-[11px] mb-2.5 leading-relaxed" style={{ color: '#7a7060' }}>
              If it does, sharing it has to go on the company’s structured digital database.
            </p>

            <div className="space-y-1.5">
              {UPSI_OPTIONS.map((option) => {
                const active = upsiStatus === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setUpsiStatus(option.value)}
                    className="w-full text-left px-3 py-2 rounded-[8px] border transition-colors"
                    style={{
                      borderColor: active ? '#145f5f' : '#ddd6c6',
                      background: active ? '#f0f6f5' : '#fff',
                    }}
                  >
                    <div className="text-[12px] font-semibold text-[#08111f]">{option.label}</div>
                    <div className="text-[10.5px] leading-relaxed" style={{ color: '#7a7060' }}>
                      {option.hint}
                    </div>
                  </button>
                );
              })}
            </div>

            {(upsiStatus === 'upsi' || upsiStatus === 'unsure') && (
              <div
                className="mt-2.5 px-3 py-2 rounded-[8px] text-[11px] leading-relaxed"
                style={{ background: '#fff7e6', color: '#8a6a1f', border: '1px solid #eadfc0' }}
              >
                A suggested register entry will be raised for the compliance officer. They choose
                the UPSI event and state the legitimate purpose before anything is recorded —
                nothing is written to the register automatically.
              </div>
            )}
          </div>

          <div className="flex justify-end mb-6">
            <Button size="sm" onClick={handleShare} disabled={!selected || !upsiStatus} loading={busy}>
              <FiUserPlus size={13} className="mr-1.5" /> Share
            </Button>
          </div>

          {/* Who already has access */}
          <div className="pt-4 border-t" style={{ borderColor: '#eee8db' }}>
            <label className="block text-[12px] font-semibold text-[#08111f] mb-2">
              People with access{shares.length ? ` (${shares.length})` : ''}
            </label>
            {loadingShares ? (
              <div className="text-[12px] py-2" style={{ color: '#7a7060' }}>Loading…</div>
            ) : shares.length === 0 ? (
              <div className="text-[12px] py-2" style={{ color: '#7a7060' }}>
                Not shared with anyone yet. Only you can see this document.
              </div>
            ) : (
              <div className="space-y-2">
                {shares.map((s) => {
                  const user = s.user && typeof s.user === 'object' ? s.user : null;
                  const userId = user ? user._id : s.user;
                  return (
                    <div key={String(userId)} className="flex items-center justify-between gap-3 px-3 py-2 rounded-[8px]" style={{ background: '#faf8f3', border: '1px solid #eee8db' }}>
                      <div className="min-w-0">
                        <div className="text-[12.5px] font-semibold text-[#08111f] truncate">
                          {user?.fullName || 'Professional'}
                        </div>
                        <div className="text-[10.5px]" style={{ color: '#7a7060' }}>
                          {user?.profession ? `${user.profession} • ` : ''}
                          {levelLabel(s.accessLevel)}
                          {/* When they got it. On a UPSI document this is the
                              sharing date the register records, so it should be
                              visible here rather than only in the SDD. */}
                          {s.sharedAt ? ` • shared ${sharedOn(s.sharedAt)}` : ''}
                        </div>
                      </div>
                      <HoverPopover variant="tooltip" content="Remove access">
                        <button
                          aria-label="Remove access"
                          className="w-7 h-7 rounded-[8px] flex items-center justify-center text-[#7a7060] transition-colors hover:bg-[#9a564a]/10 hover:text-[#9a564a]"
                          onClick={() => handleRevoke(userId)}
                          disabled={busy}
                        >
                          <FiTrash2 size={13} />
                        </button>
                      </HoverPopover>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
